import React, { useContext } from 'react';
import styled from 'styled-components';
import { ExperienceVariant, YesNo } from '@/webapi/use-experience-api';
import { Card } from '@/components/card';
import { DeviceType } from '@/utils/definitions';
import { EditorContext } from '@/features/editor/context/editor-context';
import { VSpace } from '@/components/spacing';
import { DefaultInlineVariantsLayoutHeader } from '@/features/editor/widgets/shared/inline-variants-layout/default-header';
import { InlineVariantsLayoutControlGroup } from '@/features/editor/widgets/shared/inline-variants-layout/control';
import { InlineVariantsLayoutVariation } from '@/features/editor/widgets/shared/inline-variants-layout/variant';
import { InlineVariantsLayoutAddVariantButton } from '@/features/editor/widgets/shared/inline-variants-layout/add-variant-button';
import { InlineVariantsLayoutGoals } from '@/features/editor/widgets/shared/inline-variants-layout/goals';
import { CircularCheckmark } from '@/components/circular-checkmark';
import { Alert } from '@/components/alert';
import { ProductSelectorForAnalytics } from '@/features/editor/widgets/shared/inline-variants-layout/product-analytics-selector';
// @ts-ignore
import ga4Icon from '../../../../../assets/book_icon.svg';
import { FeatureBit } from '@/webapi/use-auth-api';
import { useFeatureBit } from '@/features/account-context';
import { isPriceTestExperience } from '@/utils/experience-utils';

export interface InlineVariantsLayoutProps {
  className?: string;

  hideGoals?: boolean;
  hideGA?: boolean;

  header?: React.ReactNode;
  footer?: React.ReactNode;

  controlDescription?: React.ReactNode;
  controlAction?: React.ReactNode;
  variantAction?: (
    variant: ExperienceVariant,
    variantIdx: number,
  ) => React.ReactNode;
  variantDescription?: (
    variant: ExperienceVariant,
    variantIdx: number,
  ) => React.ReactNode;
  variantContent?: (
    variant: ExperienceVariant,
    variantIdx: number,
  ) => React.ReactNode;

  disableActiveStyles?: boolean;
}

export function InlineVariantsLayout({
  className,
  header,
  footer,
  hideGoals,
  hideGA,
  controlDescription,
  controlAction,
  variantDescription,
  variantAction,
  variantContent,
  disableActiveStyles,
}: InlineVariantsLayoutProps) {
  const {
    experienceState,
    devicePreview,
    resources: { gaIntegrationEnabled },
  } = useContext(EditorContext);
  const { currentExperience, toggleGaEvent } = experienceState;
  const isPriceTest = isPriceTestExperience(currentExperience);
  const [analyzeProducts, setAnalyzeProducts] = React.useState(false);
  const [showProductModal, setShowProductModal] = React.useState(false);
  const {
    editorState: { device },
  } = devicePreview;
  const { variants } = currentExperience;
  const gaEventsEnabled =
    currentExperience.targeting.sendGAEvents === YesNo.YES;
  const perProductAnalyticsEnabled = useFeatureBit(
    FeatureBit.ANALYZE_EXPERIENCE_SEPERATE_PRODUCTS,
  );

  return (
    <Wrapper className={className} device={device}>
      {header || <DefaultInlineVariantsLayoutHeader />}
      <VSpace value={2} />
      <InlineVariantsLayoutControlGroup
        device={device}
        description={controlDescription}
        action={controlAction}
      />
      {variants?.map((variant) => (
        <div key={variant.id}>
          <VSpace value={2} />
          <InlineVariantsLayoutVariation
            device={device}
            variant={variant}
            variantDescription={variantDescription?.(
              variant,
              variants.indexOf(variant),
            )}
            variantContent={variantContent?.(
              variant,
              variants.indexOf(variant),
            )}
            variantAction={variantAction?.(variant, variants.indexOf(variant))}
            disableActiveStyles={disableActiveStyles}
          />
        </div>
      ))}
      <InlineVariantsLayoutAddVariantButton />
      {!hideGoals && (
        <>
          <VSpace value={2} />
          <InlineVariantsLayoutGoals />
        </>
      )}
      {perProductAnalyticsEnabled && !isPriceTest && (
        <>
          <VSpace value={3} />
          <CircularCheckmark
            selected={analyzeProducts}
            onChange={(value) => {
              setAnalyzeProducts(value);
              if (value) setShowProductModal(true);
            }}
            caption={
              <span>
                Analyze experience impact on{` `}
                <ProductLink
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowProductModal(true);
                  }}
                >
                  selected products
                </ProductLink>
                {` `}separately
              </span>
            }
          />
          <ProductSelectorForAnalytics
            isVisible={showProductModal}
            onClose={() => setShowProductModal(false)}
          />
        </>
      )}
      {gaIntegrationEnabled && !hideGA && (
        <>
          <VSpace value={3} />
          <CircularCheckmark
            selected={gaEventsEnabled}
            caption="Send events to google analytics"
            onChange={toggleGaEvent}
          />
        </>
      )}
      {gaEventsEnabled && (
        <>
          <VSpace value={1.5} />
          <Alert
            type="info"
            text="How to create a segment in GA4 based on different variations >"
            icon={ga4Icon}
            href="https://help.visually.io/visually.io-google-analytics-4-integration-visually-help-center"
          />
        </>
      )}
      {footer}
    </Wrapper>
  );
}

const Wrapper = styled(Card)`
  && {
    width: ${({ device }: { device: DeviceType }) =>
      device === DeviceType.Desktop ? `100%` : `100%`};
    padding: ${({ device }: { device: DeviceType }) =>
      device === DeviceType.Desktop ? `2rem` : `3rem`};

    cursor: default;

    :hover {
      background: white;
    }

    :active {
      background: white;
    }
  }
`;

const ProductLink = styled.span`
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    opacity: 0.8;
  }
`;
